import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { alpha, useTheme } from '@mui/material/styles';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Fade,
    Grid,
    MenuItem,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import { postData, getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ProcessGuide from '../../components/orden/ProcessGuide';
import CrearPacienteDialog from '../../components/orden/CrearPacienteDialog';
import ReprogramacionCriterios from '../../components/orden/ReprogramacionCriterios';
import { FormSectionBlock, FormSectionHeader } from '../../components/ui/FormSection';

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(3),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

const STEPS = ['Paciente', 'Práctica', 'Origen', 'Confirmación'];
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 300;

function formatPlazoMinutos(min) {
    if (min == null) return '—';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
}

function pacienteOptionLabel(p) {
    if (!p) return '';
    return p.dni ? `${p.nombre} — DNI ${p.dni}` : (p.nombre || '');
}

function hasContacto(p) {
    return !!(String(p?.celular || '').trim() || String(p?.email || '').trim());
}

function contactoResumen(p) {
    const parts = [];
    if (p?.celular) parts.push(p.celular);
    if (p?.email) parts.push(p.email);
    return parts.length ? parts.join(' · ') : 'Sin contacto';
}

function Page() {
    const { classes } = useStyles();
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();
    const accent = theme.palette.info;

    const isAdmin = !!(usuario?.es_admin || usuario?.nivel_usuario === 1);
    const [activeStep, setActiveStep] = useState(0);
    const [practicas, setPracticas] = useState([]);
    const [establecimientos, setEstablecimientos] = useState([]);

    const [pacienteOptions, setPacienteOptions] = useState([]);
    const [pacienteSelected, setPacienteSelected] = useState(null);
    const [pacienteInput, setPacienteInput] = useState('');
    const [pacienteLoading, setPacienteLoading] = useState(false);
    const [searchedEmpty, setSearchedEmpty] = useState(false);
    const searchSeq = useRef(0);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogQuery, setDialogQuery] = useState('');

    useEffect(() => {
        const load = async () => {
            const [prac, est] = await Promise.all([
                getDataFull('practicas?per_page=200&solo_activas=1', usuario.token),
                isAdmin
                    ? getDataFull('establecimientos?per_page=500', usuario.token)
                    : Promise.resolve({ data: [], error: '' }),
            ]);
            if (prac.error === '' && prac.data) setPracticas(prac.data);
            if (est.error === '' && est.data) {
                setEstablecimientos((est.data || []).filter((e) => e.tipo === 'publico' && e.activo !== false));
            }
        };
        load();
    }, [usuario.token, isAdmin]);

    useEffect(() => {
        const q = pacienteInput.trim();
        if (q.length < MIN_SEARCH) {
            setPacienteOptions((prev) => {
                if (pacienteSelected && !prev.some((p) => p.id === pacienteSelected.id)) {
                    return [pacienteSelected];
                }
                return pacienteSelected ? [pacienteSelected] : [];
            });
            setSearchedEmpty(false);
            setPacienteLoading(false);
            return undefined;
        }

        const seq = ++searchSeq.current;
        setPacienteLoading(true);
        const timer = setTimeout(async () => {
            const res = await getDataFull(
                `pacientes?search_text=${encodeURIComponent(q)}&per_page=20`,
                usuario.token
            );
            if (seq !== searchSeq.current) return;
            setPacienteLoading(false);
            if (res.error !== '') {
                enqueueSnackbar(res.error, { variant: 'error' });
                setSearchedEmpty(false);
                return;
            }
            let list = Array.isArray(res.data) ? res.data : [];
            if (pacienteSelected && !list.some((p) => p.id === pacienteSelected.id)) {
                list = [pacienteSelected, ...list];
            }
            setPacienteOptions(list);
            setSearchedEmpty(list.filter((p) => !pacienteSelected || p.id !== pacienteSelected.id).length === 0);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [pacienteInput, usuario.token, pacienteSelected, enqueueSnackbar]);

    const initialValues = {
        paciente_id: '',
        practica_id: '',
        establecimiento_origen_id: '',
    };

    const schemaByStep = [
        yup.object({ paciente_id: yup.number().required('Corresponde seleccionar un paciente') }),
        yup.object({ practica_id: yup.number().required('Corresponde seleccionar una práctica') }),
        isAdmin
            ? yup.object({ establecimiento_origen_id: yup.number().required('Corresponde seleccionar el establecimiento de origen') })
            : yup.object({}),
        yup.object({}),
    ];

    const onClose = useCallback(() => navigate('/orden-practica'), [navigate]);

    const onCancel = () => {
        alertModal.showModal('Cancelar', 'Se perderán los datos cargados. ¿Desea continuar?', () => onClose());
    };

    const applyPaciente = useCallback((paciente, setFieldValue) => {
        if (!paciente?.id) return;
        setPacienteSelected(paciente);
        setPacienteOptions((prev) => {
            if (prev.some((p) => p.id === paciente.id)) return prev;
            return [paciente, ...prev];
        });
        setPacienteInput(pacienteOptionLabel(paciente));
        setSearchedEmpty(false);
        setFieldValue('paciente_id', paciente.id);
    }, []);

    const openCrearDialog = () => {
        setDialogQuery(pacienteInput.trim());
        setDialogOpen(true);
    };

    const submitCreate = async (values) => {
        loadingModal.setOpen(true);
        try {
            const payload = {
                paciente_id: Number(values.paciente_id),
                practica_id: Number(values.practica_id),
            };
            if (isAdmin) {
                payload.establecimiento_origen_id = Number(values.establecimiento_origen_id);
            }
            const response = await postData('ordenes-practicas', payload, usuario.token);
            if (response.error === '') {
                enqueueSnackbar('Orden creada correctamente', { variant: 'success' });
                const id = response.data?.id;
                navigate(id ? `/orden-practica/${id}` : '/orden-practica');
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Error al crear la orden', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const visibleSteps = useMemo(
        () => (isAdmin ? STEPS : STEPS.filter((s) => s !== 'Origen')),
        [isAdmin]
    );

    const mapStepIndex = (uiStep) => {
        if (isAdmin) return uiStep;
        if (uiStep <= 1) return uiStep;
        return 3;
    };

    const guideSteps = useMemo(() => {
        const steps = [
            'Buscar el paciente por nombre o DNI. Crear uno nuevo solo si no figura.',
            'Seleccionar la práctica y revisar los plazos.',
        ];
        if (isAdmin) {
            steps.push('Indicar el establecimiento público de origen.');
        }
        steps.push('Revisar el resumen y crear la orden.');
        return steps;
    }, [isAdmin]);

    const showSearchTip = !pacienteSelected && pacienteInput.trim().length < MIN_SEARCH;

    return (
        <Layout nombrePagina="Nueva orden de práctica">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <Box
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            background: `linear-gradient(135deg, ${alpha(accent.main, 0.08)} 0%, ${alpha(accent.main, 0.02)} 55%, transparent 100%)`,
                        }}
                    >
                        <Box
                            sx={{
                                height: 5,
                                background: `linear-gradient(90deg, ${accent.dark || accent.main} 0%, ${alpha(accent.main, 0.55)} 55%, transparent 100%)`,
                            }}
                        />
                        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                                <Box
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        bgcolor: alpha(accent.main, 0.18),
                                        color: accent.main,
                                    }}
                                >
                                    <AssignmentIcon fontSize="medium" />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                                        Nueva orden de práctica
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
                                        Permite asociar un paciente existente o dar de alta uno nuevo, elegir la práctica y confirmar el pedido.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>

                    <Paper className={classes.paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                        <ProcessGuide title="Cómo completar la orden" steps={guideSteps} />

                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                            {visibleSteps.map((label) => (
                                <Step key={label}><StepLabel>{label}</StepLabel></Step>
                            ))}
                        </Stepper>

                        <Formik
                            initialValues={initialValues}
                            validationSchema={schemaByStep[mapStepIndex(activeStep)]}
                            onSubmit={async (values) => {
                                const internal = mapStepIndex(activeStep);
                                if (internal < 3) {
                                    setActiveStep((s) => s + 1);
                                    return;
                                }
                                await submitCreate(values);
                            }}
                        >
                            {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => {
                                const practica = practicas.find((p) => p.id === Number(values.practica_id));
                                const origen = establecimientos.find((e) => e.id === Number(values.establecimiento_origen_id));
                                const internal = mapStepIndex(activeStep);
                                const paciente = pacienteSelected?.id === Number(values.paciente_id)
                                    ? pacienteSelected
                                    : null;

                                return (
                                    <>
                                        <Form>
                                            {internal === 0 && (
                                                <Box>
                                                    <FormSectionHeader
                                                        accentColor="primary"
                                                        icon={PersonSearchIcon}
                                                        title="Paciente"
                                                        description="Se busca un paciente existente. Solo se crea uno nuevo si no aparece en los resultados."
                                                    />
                                                    <Autocomplete
                                                        options={pacienteOptions}
                                                        loading={pacienteLoading}
                                                        value={pacienteSelected}
                                                        inputValue={pacienteInput}
                                                        filterOptions={(x) => x}
                                                        getOptionLabel={pacienteOptionLabel}
                                                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                                        onInputChange={(_e, value, reason) => {
                                                            if (reason === 'reset') return;
                                                            setPacienteInput(value);
                                                        }}
                                                        onChange={(_e, option) => {
                                                            if (option) {
                                                                applyPaciente(option, setFieldValue);
                                                            } else {
                                                                setPacienteSelected(null);
                                                                setFieldValue('paciente_id', '');
                                                            }
                                                        }}
                                                        noOptionsText={
                                                            pacienteInput.trim().length < MIN_SEARCH
                                                                ? 'Escribir al menos 2 caracteres'
                                                                : (pacienteLoading ? 'Buscando…' : 'Sin coincidencias')
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Buscar paciente existente"
                                                                variant="standard"
                                                                placeholder="Nombre o DNI"
                                                                error={touched.paciente_id && Boolean(errors.paciente_id)}
                                                                helperText={
                                                                    (touched.paciente_id && errors.paciente_id)
                                                                    || 'Nombre o DNI (mínimo 2 caracteres).'
                                                                }
                                                            />
                                                        )}
                                                    />

                                                    {showSearchTip ? (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                                            No es necesario crear un paciente nuevo si ya está registrado.
                                                        </Typography>
                                                    ) : null}

                                                    {pacienteSelected ? (
                                                        <Box
                                                            sx={{
                                                                mt: 2,
                                                                p: 2,
                                                                borderRadius: 2,
                                                                border: (t) => `1px solid ${t.palette.success.light}`,
                                                                bgcolor: (t) => alpha(t.palette.success.main, 0.06),
                                                            }}
                                                        >
                                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                                                                <Chip size="small" color="success" label="Seleccionado" />
                                                            </Stack>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {pacienteSelected.nombre}
                                                                {pacienteSelected.dni ? ` · DNI ${pacienteSelected.dni}` : ''}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {contactoResumen(pacienteSelected)}
                                                            </Typography>
                                                            {!hasContacto(pacienteSelected) ? (
                                                                <Alert severity="warning" sx={{ mt: 1 }}>
                                                                    El paciente no tiene celular ni correo. Corresponde completar el contacto en la ficha antes de crear pedidos que requieran aviso de turno.
                                                                </Alert>
                                                            ) : null}
                                                        </Box>
                                                    ) : null}

                                                    {searchedEmpty && !pacienteSelected ? (
                                                        <Alert
                                                            severity="info"
                                                            sx={{ mt: 2 }}
                                                            action={
                                                                <Button color="inherit" size="small" variant="contained" onClick={openCrearDialog}>
                                                                    Crear paciente
                                                                </Button>
                                                            }
                                                        >
                                                            No hay coincidencias. Corresponde crear el paciente para continuar.
                                                        </Alert>
                                                    ) : null}

                                                    <Box sx={{ mt: 2 }}>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={openCrearDialog}
                                                        >
                                                            Crear paciente nuevo
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            )}

                                            {internal === 1 && (
                                                <Box>
                                                    <FormSectionHeader
                                                        accentColor="secondary"
                                                        icon={MedicalServicesIcon}
                                                        title="Práctica"
                                                        description="Define el estudio o procedimiento y los plazos de la orden."
                                                    />
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        variant="standard"
                                                        label="Práctica"
                                                        name="practica_id"
                                                        value={values.practica_id}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.practica_id && Boolean(errors.practica_id)}
                                                        helperText={
                                                            (touched.practica_id && errors.practica_id)
                                                            || 'Se listan las prácticas activas disponibles.'
                                                        }
                                                    >
                                                        {practicas.map((p) => (
                                                            <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                    {practica ? (
                                                        <Alert severity="info" sx={{ mt: 2 }}>
                                                            <Typography variant="body2">
                                                                Plazo de respuesta: <b>{formatPlazoMinutos(practica.plazo_respuesta_minutos)}</b>
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Plazo máximo sin turno: <b>{formatPlazoMinutos(practica.plazo_max_sin_turno_minutos)}</b>
                                                            </Typography>
                                                            {practica.requiere_insumo ? (
                                                                <Typography variant="body2">
                                                                    Requiere insumo — plazo de compra: <b>{practica.plazo_compra_horas} h</b>
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2">No requiere compra de insumo previa.</Typography>
                                                            )}
                                                            <Box sx={{ mt: 1.5 }}>
                                                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                                                                    Límites de reprogramación
                                                                </Typography>
                                                                <ReprogramacionCriterios
                                                                    readOnly
                                                                    ordenOrPractica={{
                                                                        reprogramaciones_max: practica.reprogramaciones_max ?? 2,
                                                                        reprogramaciones_count: 0,
                                                                        reprogramacion_antelacion_horas: practica.reprogramacion_antelacion_horas ?? 24,
                                                                        reprogramacion_cooldown_horas: practica.reprogramacion_cooldown_horas ?? 12,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Alert>
                                                    ) : null}
                                                </Box>
                                            )}

                                            {internal === 2 && isAdmin && (
                                                <Box>
                                                    <FormSectionHeader
                                                        accentColor="warning"
                                                        icon={BusinessIcon}
                                                        title="Origen"
                                                        description="Establecimiento público desde el que se genera el pedido."
                                                    />
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        variant="standard"
                                                        label="Establecimiento de origen"
                                                        name="establecimiento_origen_id"
                                                        value={values.establecimiento_origen_id}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.establecimiento_origen_id && Boolean(errors.establecimiento_origen_id)}
                                                        helperText={
                                                            (touched.establecimiento_origen_id && errors.establecimiento_origen_id)
                                                            || 'Solo establecimientos públicos activos.'
                                                        }
                                                    >
                                                        {establecimientos.map((e) => (
                                                            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Box>
                                            )}

                                            {internal === 3 && (
                                                <FormSectionBlock
                                                    accentColor="success"
                                                    icon={CheckCircleOutlineIcon}
                                                    title="Confirmación"
                                                    description="Revisión final antes de crear la orden."
                                                    sx={{ mb: 0 }}
                                                >
                                                    <Typography variant="body2">Paciente: {paciente?.nombre || '—'}</Typography>
                                                    <Typography variant="body2">Práctica: {practica?.nombre || '—'}</Typography>
                                                    {isAdmin ? (
                                                        <Typography variant="body2">Origen: {origen?.nombre || '—'}</Typography>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            Origen: {usuario?.establecimiento?.nombre || 'Establecimiento de sesión'}
                                                        </Typography>
                                                    )}
                                                </FormSectionBlock>
                                            )}

                                            <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                                                <Button onClick={onCancel} disabled={isSubmitting}>Cancelar</Button>
                                                {activeStep > 0 ? (
                                                    <Button onClick={() => setActiveStep((s) => s - 1)} disabled={isSubmitting}>
                                                        Anterior
                                                    </Button>
                                                ) : null}
                                                <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting}>
                                                    {internal === 3 ? 'Crear orden' : 'Siguiente'}
                                                </Button>
                                            </Stack>
                                        </Form>

                                        <CrearPacienteDialog
                                            open={dialogOpen}
                                            onClose={() => setDialogOpen(false)}
                                            token={usuario.token}
                                            initialQuery={dialogQuery}
                                            onCreated={(p) => {
                                                applyPaciente(p, setFieldValue);
                                                enqueueSnackbar('Paciente creado y seleccionado.', { variant: 'success' });
                                            }}
                                            onSelectExisting={(p) => {
                                                applyPaciente(p, setFieldValue);
                                                enqueueSnackbar('Paciente seleccionado.', { variant: 'info' });
                                            }}
                                        />
                                    </>
                                );
                            }}
                        </Formik>
                    </Paper>
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
